import type { EventItem } from "@/mocks/events";

interface StatusBadgeProps {
  status: string;
}

export function StatusBadge({ status }: StatusBadgeProps) {
  const styles: Record<string, string> = {
    进行中: "bg-accent-500 text-foreground-950",
    报名中: "bg-primary-100 text-primary-800",
    已结束: "bg-secondary-200 text-secondary-800",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
        styles[status] || "bg-background-200 text-foreground-700"
      }`}
    >
      {status === "进行中" && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-foreground-950"></span>
      )}
      {status}
    </span>
  );
}

interface EventCardProps {
  event: EventItem;
  layout?: "vertical" | "horizontal";
}

export function EventCard({ event, layout = "vertical" }: EventCardProps) {
  if (layout === "horizontal") {
    return (
      <a
        href={`/detail/${event.id}`}
        className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 sm:flex-row"
      >
        <div className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-48">
          <img
            src={event.cover}
            alt={`${event.title} 赛事封面`}
            title={`${event.title} 赛事资讯`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-2">
            <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800">
              {event.category}
            </span>
            <StatusBadge status={event.status} />
          </div>
          <h3 className="mt-2 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
            {event.title}
          </h3>
          <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{event.summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {event.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              {event.region}
            </span>
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={`/detail/${event.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-300"
    >
      <div className="relative h-44 w-full overflow-hidden bg-background-200">
        <img
          src={event.cover}
          alt={`${event.title} 赛事封面`}
          title={`${event.title} 赛事资讯`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex items-center gap-2">
          <StatusBadge status={event.status} />
        </div>
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-foreground-950/70 px-2.5 py-1 text-xs text-background-50 backdrop-blur">
          <i className="ri-fire-line text-accent-400"></i>
          {event.heat}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded bg-primary-50 px-2 py-0.5 font-medium text-primary-700">
            {event.category}
          </span>
          <span className="text-foreground-500">{event.format}</span>
        </div>
        <h3 className="mt-3 line-clamp-2 font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
          {event.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-foreground-600">{event.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {event.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-map-pin-line"></i>
            {event.region}
          </span>
        </div>
      </div>
    </a>
  );
}

export default EventCard;